import React, { useCallback, useEffect, useState } from "react";
import type ReactECharts from "echarts-for-react";
import type { TooltipSnapshot } from "../utils/tooltipSnapshot";

const COLUMN_LABELS = ["A", "B", "C", "D", "E", "F"];

interface MarkerPos {
  key: number;
  label: string;
  left: number;
  isReference: boolean;
}

interface Props {
  points: TooltipSnapshot[];
  referenceDataIndex: number | null;
  chartRef: React.RefObject<ReactECharts>;
  /** Bumped on datazoom so markers track the moving x-axis. */
  repositionTick: number;
}

/**
 * Vertical guide markers drawn on the chart at each compared point's
 * x-position. Mirrors the CriticalBandLabels approach: absolutely-positioned
 * HTML driven by convertToPixel, recomputed on tick/resize. Does NOT touch the
 * ECharts `graphic` channel (reserved for editable handles).
 */
export function CompareMarkers({
  points,
  referenceDataIndex,
  chartRef,
  repositionTick,
}: Props) {
  const [positions, setPositions] = useState<MarkerPos[]>([]);
  const [gridBox, setGridBox] = useState<{ y: number; h: number } | null>(null);

  const recompute = useCallback(() => {
    const inst = chartRef.current?.getEchartsInstance?.();
    if (!inst || inst.isDisposed?.() || points.length === 0) {
      setPositions([]);
      setGridBox(null);
      return;
    }

    let gridRect: { x: number; y: number; w: number; h: number } | null = null;
    try {
      const model = (inst as any).getModel?.();
      const coord = model?.getComponent?.("grid")?.coordinateSystem;
      const r = coord?.getRect?.();
      if (r) gridRect = { x: r.x, y: r.y, w: r.width, h: r.height };
    } catch {
      gridRect = null;
    }

    const pxOf = (index: number): number | null => {
      let p: any;
      try {
        p = inst.convertToPixel({ xAxisIndex: 0 }, index);
      } catch {
        return null;
      }
      const v = Array.isArray(p) ? p[0] : (p as number);
      return Number.isFinite(v) ? v : null;
    };

    const next: MarkerPos[] = [];
    points.forEach((snap, idx) => {
      const left = pxOf(snap.dataIndex);
      if (left === null) return;
      // Skip markers scrolled outside the visible plot area.
      if (gridRect && (left < gridRect.x - 1 || left > gridRect.x + gridRect.w + 1)) {
        return;
      }
      next.push({
        key: snap.dataIndex,
        label: COLUMN_LABELS[idx] ?? `P${idx + 1}`,
        left,
        isReference: snap.dataIndex === referenceDataIndex,
      });
    });

    setPositions(next);
    setGridBox(gridRect ? { y: gridRect.y, h: gridRect.h } : null);
  }, [points, referenceDataIndex, chartRef]);

  // Recompute on tick change. Double-pass: rAF for first paint, 220ms for grid layout.
  useEffect(() => {
    const id1 = requestAnimationFrame(recompute);
    const id2 = window.setTimeout(recompute, 220);
    return () => {
      cancelAnimationFrame(id1);
      clearTimeout(id2);
    };
  }, [recompute, repositionTick]);

  // Reposition on chart resize — DOM-driven, not a React render loop.
  useEffect(() => {
    const inst = chartRef.current?.getEchartsInstance?.();
    const dom = inst?.getDom?.() as HTMLElement | null;
    if (!dom) return;
    const ro = new ResizeObserver(() => requestAnimationFrame(recompute));
    ro.observe(dom);
    return () => ro.disconnect();
  }, [recompute, points]);

  if (positions.length === 0 || !gridBox) return null;

  return (
    <div className="remix-compare-markers" aria-hidden>
      {positions.map((p) => (
        <div
          key={p.key}
          className={`remix-compare-marker${p.isReference ? " is-reference" : ""}`}
          style={{ left: `${p.left}px`, top: `${gridBox.y}px`, height: `${gridBox.h}px` }}
        >
          <span className="remix-compare-marker-line" />
          <span className="remix-compare-marker-badge">
            {p.isReference && (
              <span className="remix-compare-marker-pin" aria-hidden>
                📌
              </span>
            )}
            {p.label}
          </span>
        </div>
      ))}
    </div>
  );
}
